// AI Tournament Assistant — frontend wrapper around the processAICommand
// Firebase callable. The Anthropic API key NEVER lives in this bundle —
// it sits in Firebase Secret Manager and is read by the function at
// runtime. See functions/index.js.
//
// Public API:
//   loadTournamentContext(db, tournamentId)
//     → { info, flights, players, teeTimes }  // small payload for the AI
//   askAI(command, tournamentContext)
//     → { understood, action, params, confirmation, error }

import { getFunctions, httpsCallable } from 'firebase/functions'
import { ref, get } from 'firebase/database'
import { app, tref } from '../lib/firebase.js'

// Lazy so the SDK isn't initialized until the user actually opens the chat.
let _callable = null
function getCallable() {
  if (_callable) return _callable
  const fns = getFunctions(app, 'us-central1')
  _callable = httpsCallable(fns, 'processAICommand')
  return _callable
}

// Pull a slimmed-down snapshot of the active tournament. We strip score
// arrays, photos, calcutta bids, etc. — Sonnet doesn't need them and the
// payload would be enormous on a multi-day event.
export async function loadTournamentContext(db, tournamentId) {
  if (!tournamentId) return {}
  const snap = await get(tref(`tournaments/${tournamentId}`))
  const t = snap.val() || {}
  return {
    info: t.info || {},
    flights: slimFlights(t.flights || {}),
    players: slimPlayers(t.players || {}),
    teeTimes: t.teeTimes || {},
  }
}

function slimFlights(flights) {
  const out = {}
  for (const [fid, f] of Object.entries(flights || {})) {
    const teams = {}
    for (const [tid, team] of Object.entries(f?.teams || {})) {
      // Keep ONLY info — drop scores, locked timestamps, etc.
      teams[tid] = { info: team?.info || {} }
    }
    out[fid] = { info: f?.info || {}, teams }
  }
  return out
}

function slimPlayers(players) {
  const out = {}
  for (const [pid, p] of Object.entries(players || {})) {
    if (!p) continue
    out[pid] = {
      name: p.name,
      handicap: p.handicap ?? null,
      handicapIndex: p.handicapIndex ?? null,
      ghin: p.ghin || null,
    }
  }
  return out
}

// Fire the command at the callable. Always resolves; surfaces network /
// auth errors as { understood:false, error:... } so the chat UI has one
// shape to render.
export async function askAI(command, tournamentContext, options = {}) {
  try {
    const callable = getCallable()
    const payload = { command, tournamentContext }
    if (options.demoMode) payload.demoMode = options.demoMode
    const res = await callable(payload)
    const data = res?.data || {}
    if (typeof data !== 'object' || data === null) {
      return { understood: false, error: 'Empty response from AI.' }
    }
    return data
  } catch (err) {
    return {
      understood: false,
      error: `AI request failed: ${err?.message || String(err)}`,
    }
  }
}
